import { LANGUAGES_MAPPING } from './enums/languages';


const PARSER = new DOMParser();
const DATE_ISO_REGEX = /^(\d{4}-\d{2}-\d{2})(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?(([+-]\d{2}:\d{2})|(Z))?$/; //NOSONAR

class Utils {

    /**
     * Convert lang code 3 chars to lang code 2 chars.
     *
     * @param lang
     */
    convertLangCode(lang) {
        return LANGUAGES_MAPPING[lang] || lang;
    }

    /**
     * Construit une query string avec encodage personnalisé.
     * Encode tous les caractères interdits sauf le slash (/).
     * Utilisé pour contourner l'encodage automatique d'axios.
     * 
     * @param {Object} params - Les paramètres à encoder
     * @returns {string} La query string encodée
     */
    buildCustomParamsForAxios(params) {
        return Object.entries(params)
            .map(([key, value]) => {
                // Encodage personnalisé : échappe tous les caractères interdits sauf /
                let encodedValue = String(value)
                    .replaceAll('%', '%25')
                    .replaceAll(' ', '%20')
                    .replaceAll('!', '%21')
                    .replaceAll('"', '%22')
                    .replaceAll('#', '%23')
                    .replaceAll('$', '%24')
                    .replaceAll('&', '%26')
                    .replaceAll("'", '%27')
                    .replaceAll('(', '%28')
                    .replaceAll(')', '%29')
                    .replaceAll('*', '%2A')
                    .replaceAll('+', '%2B')
                    .replaceAll(',', '%2C')
                    .replaceAll(':', '%3A')
                    .replaceAll(';', '%3B')
                    .replaceAll('<', '%3C')
                    .replaceAll('=', '%3D')
                    .replaceAll('>', '%3E')
                    .replaceAll('?', '%3F')
                    .replaceAll('@', '%40')
                    .replaceAll('[', '%5B')
                    .replaceAll('\\', '%5C')
                    .replaceAll(']', '%5D')
                    .replaceAll('^', '%5E')
                    .replaceAll('`', '%60')
                    .replaceAll('{', '%7B')
                    .replaceAll('|', '%7C')
                    .replaceAll('}', '%7D')
                    .replaceAll('~', '%7E');
                
                // Encode les caractères accentués et non-ASCII (le flag 'u' gère correctement les emojis)
                // eslint-disable-next-line no-control-regex
                encodedValue = encodedValue.replaceAll(/[^\x00-\x7F]/gu, char => encodeURIComponent(char));
                
                return `${encodeURIComponent(key)}=${encodedValue}`;
            })
            .join('&');
    }

    /**
     * Remove blank item in array.
     */
    removeBlankItemInArray(array) {
        return array.filter((item, index) => {
            if (item) return array.indexOf(item) == index;
        });
    }

    /**
     * Sort an array by a specific order.
     * In case of array of objects, a key can be used to sort by a specific order on a specific key.
     */
    sortArrayBySpecificOrder(arrayData, arrayOrder, key = undefined) {
        return arrayData
            .sort((a, b) => {

                // If a key is defined, take into account this key in the sorting.
                if (key) {
                    a = a[key];
                    b = b[key];
                }

                // Both a and b exists in the array order, so sort between them
                if (arrayOrder.indexOf(a) !== -1 && arrayOrder.indexOf(b) !== -1) {
                    return arrayOrder.indexOf(a) - arrayOrder.indexOf(b);
                }

                // At least one a or b does not exists in the array order, so sort globally
                if (arrayOrder.indexOf(a) < arrayOrder.indexOf(b)) {
                    return 1;
                } else if (arrayOrder.indexOf(a) > arrayOrder.indexOf(b)) {
                    return -1;
                } else {
                    return 0;
                }
            });
    }

    /**
     * Extract an attribut's value inside a nested object by indicating the exact "path" in string.
     * @param {*} object Target nested object
     * @param {*} string The "path" of desired attribut
     * @returns Attribut's value
     */
    getAttributInNestedObject(object, string) {
        string = string.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        string = string.replace(/^\./, '');           // strip a leading dot
        var attributs = string.split('.');
        for (var i = 0, n = attributs.length; i < n; ++i) {
            var key = attributs[i];
            if (key in object) {
                object = object[key];
            } else {
                return;
            }
        }
        return object;
    }

    convertObjectValuesToArray(object) {
        return (object instanceof Object) ? Object.values(object).map(value => value ? value : undefined) : [];
    }

    /**
     * Set a string to CamelCase.
     * @param str
     * @returns {*}
     */
    camelize(str) {
        return str
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove diacritics
            .replace(/\W+(.)/g, function (match, chr) {
                return chr.toUpperCase();
            });
    }

    /**
     * Compare boolean value, true < false.
     * @param {Boolean} a 
     * @param {Boolean} b 
     * @returns 
     */
    compareBoolean(a, b) {
        // true values first
        return (a === b) ? 0 : a ? -1 : 1;
    }

    /**
     * Compare string in a given language.
     * @param {*} a 
     * @param {*} b 
     * @param {*} lang code of the actual language
     * @param {*} isNumeric 
     * @returns 
     */
    compareString(a, b, lang, isNumeric) {
        if (a === b) return 0;
        if (!a) return -1;
        if (!b) return 1;
        return a.localeCompare(b, lang, { sensitivity: 'base', numeric: isNumeric });
    }

    /**
     * Clean a string by considering the following rules :
     * - Remove line breaks, carriage returns and tabs ;
     * - Delete HTML tags ;
     * - Remove spaces ;
     * - Remove diacritic signs ;
     * - Remove punctuation signs.
     */
    cleanString(text, removePunctuation = true) {
        // Remove line breaks, carriage returns and tabs
        if (text !== undefined) {
            text = text.replace(/[\n\t\r]/g, '');

            // Delete HTML tags
            let textAsHtml = PARSER.parseFromString(text, 'text/html');
            text = textAsHtml.body.innerText || textAsHtml.body.textContent; // textContent for JEST TU

            // Remove spaces
            text = text.replace(/ /g, '');

            // Remove diacritic signs
            text = text.normalize("NFD").replace(/\p{Diacritic}/gu, "");

            // Remove punctuation signs
            if (removePunctuation) {
                text = text.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
            }
        } else {
            text = "";
        }

        return text;
    }

    isHtmlValue(string) {
        let stringAsHtml = PARSER.parseFromString(string, 'text/html');
        return (stringAsHtml.body.childElementCount !== 0);
    }

    /**
     * Escape HTML characters for a given string to display it as plain text.
     * @param {*} string, the string to escape
     */
    escapeHtml(string) {
        return string.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    /**
     * Delete plain text string in case of the same string in HTML exists.
     */
    deletePlainTextWhenHtmlExists(arrayOfValues) {
        let indexToDelete = [];

        let cleanedValues = arrayOfValues ? arrayOfValues.map(text => this.cleanString(text)) : [];
        let htmlValues = arrayOfValues ? arrayOfValues.map(text => this.isHtmlValue(text)) : [];

        arrayOfValues.forEach((cText, cIndex) => {
            let cleanedText = this.cleanString(cText);

            if (!htmlValues[cIndex]) { // Check if the current string is not in HTML format
                // if not, remove if a html duplicate exist
                let cleanedNonHtmlValuesExcludingCurrentValue = cleanedValues.filter((value, index) => index !== cIndex && htmlValues[index]);

                if (cleanedNonHtmlValuesExcludingCurrentValue.some(value => value.toLowerCase() === cleanedText.toLowerCase())) {
                    indexToDelete.push(cIndex);
                }
            }
        });

        indexToDelete.forEach(index => arrayOfValues.splice(index, 1, ""));
        arrayOfValues = arrayOfValues.filter(textValue => textValue !== "");

        return arrayOfValues;
    }

    /**
     * Parse une valeur literale (chaine de caractères) en date.
     * On support les dates au format xsd:date et xsd:dateTime, qui peuvent être vues comme des dates ISO partielles.
     * Si la date au format ISO est partielle, on la complète avec la timezone Z et l'heure à 00:00:00.
     * 
     * @param {[*]} literal 
     * @returns {Date} la date parsée ou undefined si la date n'est pas valide
     */
    parseLiteralAsDate(literal) {
        // On parse la date au format ISO avec heure et timezone avec une regex pour vérifier si elle est partielle
        const dateGroups = DATE_ISO_REGEX.exec(literal);
        if (dateGroups !== null) {
            const datePart = dateGroups[1];         // Groupe 1 = la date "yyyy-mm-dd"
            const timePart = dateGroups[2];         // Groupe 2 = l'heure "T09:30:10".
            const tzPart = dateGroups[5];           // Exemple : "Z", "+06:00", "-06:00"

            let dateCompleted = datePart;

            if (!timePart) {
                // Aucun horaire fourni
                if (!tzPart || tzPart === 'Z') {
                    // Pas de fuseau horaire ou fuseau "Z" → on ajoute T12:00:00 puis Z
                    dateCompleted += 'T12:00:00Z';
                } else {
                    // Fuseau horaire de type "+/-HH:MM" → on ajoute T00:00:00 puis le fuseau horaire
                    dateCompleted += 'T00:00:00' + tzPart;
                }
            } else {
                // Un horaire est fourni → on l'utilise
                dateCompleted += timePart;
                dateCompleted += tzPart ? tzPart : 'Z';
            }

            const date = new Date(dateCompleted);
            if (date != "Invalid Date") {
                return date;
            }
        }
        return undefined;
    }
    
     /**
     * Vérifie si une valeur n'est ni une date ni un nombre.
     *
     * @param {Object} value - L'objet contenant la valeur à vérifier.
     * @param {string} value.content - La chaîne de caractères représentant la valeur.
     * @returns {boolean} - Retourne `true` si ce n'est ni une date ni un nombre, sinon `false`.
     */
    isNotDateOrNumeric(value) { 
        if (!value || !value.content) return false;
        
        const isNumeric = !isNaN(value.content);
        const isDate = this.parseLiteralAsDate(value.content);

        return !(isNumeric || isDate !== undefined);
    }
    
    /**
     * Vérifie si la valeur n’est pas un URI sans label localisé.
     * Retourne true si :
     * - la propriété 'type' est absente ou falsy, alors on considère que c'est un litteral
     * - ou si c’est un URI avec une localisation (localise === true)
    */
    isNotUriWithoutLabel(value) {
        return !value || !value.type || (value.type === 'uri' && value.localise === true);
    }
    
    /**
     * Vérifie si la valeur n’est pas un lien externe (http ou https).
    */
    isNotExternalLink(value) {
        if (!value || !value.content) return true;
        // Expression régulière pour détecter un lien <a> avec un href commençant par http ou https
        const regex = /<a\s[^>]*href=["']https?:\/\/[^"']+["']/i;
        return !regex.test(value.content);
    }
    
    /**
     * Retourne true si la valeur doit être considérée comme "non localisée".
    */
    isUnlocalizedValue(value) {
        return (this.isNotDateOrNumeric(value) &&
               this.isNotExternalLink(value) &&
               this.isNotUriWithoutLabel(value));
    }
      
    /**
     * Extrait les valeurs de langue à partir d'un objet donné et de ses nœuds blancs.
     *
     * @param {Object} currentObject - L'objet à analyser.
     * @param {Object|null} blanckNodes - Les nœuds blancs à analyser (optionnels).
     * @returns {string[]} - Un tableau contenant les langues extraites en majuscules.
     */
    extractLangValues(currentObject, blanckNodes) {
        let langues = [];
        const self = this;
        function recursiveExtract(currentObject) {
            for (const key in currentObject) {
                if (key === 'axiomAnnotations') continue;
                if (typeof currentObject[key] === 'object' && currentObject[key] !== null) {
                    recursiveExtract(currentObject[key]);
                } else if (key === 'lang' && self.isUnlocalizedValue(currentObject)) {
                    langues.push(currentObject[key] ? currentObject[key].toUpperCase() : 'UND');
                }
            }
        }
        recursiveExtract(blanckNodes);
        recursiveExtract(currentObject);
        return langues;
    }

    /**
     * Trie un tableau de langues selon un ordre spécifique et ajoute les éléments restants à la fin.
     * 
     * @param {string[]} languagesArray - Le tableau de langues à trier.
     * @param {string[]} priorityOrder - L'ordre de tri prioritaire.
     * @returns {string[]} - Un nouveau tableau trié.
    */
    sortArrayByPriorityOrder(languagesArray, priorityOrder) {
        let sortedLanguages = [...languagesArray];
        let result = [];

        for (let lang of priorityOrder) {
            if (sortedLanguages.includes(lang)) {
                result.push(lang);
                sortedLanguages.splice(sortedLanguages.indexOf(lang), 1);
            }
        }

        // Ajouter les langues restantes non triées
        result.push(...sortedLanguages);

        return result;
    }

    /**
     * Traite les langues extraites et applique un tri basé sur la configuration.
     *
     * @param {Object} blankNodes - Les nœuds blancs contenant les langues.
     * @param {Object} storeConfiguration - La configuration de l'affichage des langues.
     * @param {Object|null} [data=null] - Les données supplémentaires à analyser (optionnel).
     * @returns {Object} - Un objet contenant :
     *                     - `languages` : Un tableau des langues triées.
     *                     - `isOnlyNotLocalizedLang` : Un booléen indiquant si les langues ne sont pas localisées.
     */
    processLanguages(blankNodes, storeConfiguration, data = null) {
        let langues = this.extractLangValues(JSON.parse(JSON.stringify(data)), JSON.parse(JSON.stringify(blankNodes)));
        let uniqueValues = new Set(langues);
        let sortedLanguages = Array.from(uniqueValues).slice().sort();
        let languages = new Array();
        let languagesConf = this.convertObjectValuesToArray(storeConfiguration.current && storeConfiguration.current.concepts_details && storeConfiguration.current.concepts_details.affichage ? storeConfiguration.current.concepts_details.affichage.ordre_langues : null);
        languagesConf = languagesConf.map(language => language.toUpperCase());
        languages = this.sortArrayByPriorityOrder(sortedLanguages, languagesConf);

        // Déterminer si c'est uniquement des langues non localisées
        let isOnlyNotLocalizedLang = null;
        if (languages.length === 0 || (languages.length === 1 && (languages[0].toUpperCase() === "UND" || languages[0].toUpperCase() === "NULL"))) {
            isOnlyNotLocalizedLang = false;
            languages = [];
        } else {
            isOnlyNotLocalizedLang = true;
        }

        return { languages, isOnlyNotLocalizedLang };
    }

    /**
     * Retournes le text d'un lien hypertexte
     * @param {*} url 
     * @returns 
     */
    extractLabelFromHyperlink(url){
        if (!url) {
            return url;
        }
        return url.replace(/<a href="([^"]+)">([^<]+)<\/a>/g, '$2')
    }

}

export default new Utils();